import { useEffect, useState } from "react";
import { MdVisibility, MdEdit, MdDelete, MdCheckCircle, MdError, MdClose, MdWarning } from "react-icons/md";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { canDo } from "../../utils/permissions";

// Pulls the friendliest message out of an API error: field-level validation
// details first, then the backend message, then the raw text.
export function errMessage(e) {
  const raw = e?.message;
  if (!raw) return "មានបញ្ហាមិនស្គាល់ — សូមព្យាយាមម្តងទៀត";
  try {
    const o = e?.body || JSON.parse(raw);
    if (o?.data && typeof o.data === "object") {
      const parts = Object.values(o.data).filter(Boolean);
      if (parts.length) return parts.join(" · ");
    }
    return o?.message || raw;
  } catch {
    return raw;
  }
}

export function passwordChecks(pw = "") {
  return {
    length:    pw.length >= 8,
    lowercase: /[a-z]/.test(pw),
    uppercase: /[A-Z]/.test(pw),
    number:    /[0-9]/.test(pw),
    symbol:    /[^A-Za-z0-9]/.test(pw),
  };
}

export function passwordValid(pw = "") {
  return Object.values(passwordChecks(pw)).every(Boolean);
}

const PASSWORD_PART_LABELS = {
  length:    "យ៉ាងហោចណាស់ ៨ តួអក្សរ",
  uppercase: "អក្សរធំ",
  lowercase: "អក្សរតូច",
  number:    "លេខ (0-9)",
  symbol:    "និមិត្តសញ្ញាពិសេស (ឧទាហរណ៍៖ @, #, $, %)",
};

export function PasswordStrength({ password }) {
  const { t, lang } = useLanguage();
  if (!password) return null;
  const c = passwordChecks(password);
  const missing = Object.keys(PASSWORD_PART_LABELS).filter(k => !c[k]);
  if (missing.length === 0) return null;

  const labels = missing.map(k => t(PASSWORD_PART_LABELS[k]));
  const joiner = lang === "km" ? "" : " ";
  const text = labels.length === 1
    ? labels[0]
    : `${labels.slice(0, -1).join(" ")} ${t("និង")}${joiner}${labels[labels.length - 1]}`;

  const KH = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
  return (
    <p style={{ fontSize:11, color:"#EF4444", marginTop:5, fontFamily:KH,
                display:"flex", alignItems:"flex-start", gap:4, lineHeight:1.5 }}>
      <MdWarning size={13} style={{ flexShrink:0, marginTop:1 }} />
      {t("ពាក្យសម្ងាត់ត្រូវមាន")}{text}
    </p>
  );
}

const statusMap = {
  "កំពុងបម្រើការ":      { bg:"#D1FAE5", color:"#065F46" },
  "ចូលប្រើបាន":         { bg:"#D1FAE5", color:"#065F46" },
  "ដំណើរការ":           { bg:"#D1FAE5", color:"#065F46" },
  "សកម្ម":              { bg:"#D1FAE5", color:"#065F46" },

  "សាកល្បង":            { bg:"#DBEAFE", color:"#1E40AF" },
  "តាមកំណត់ពេល":        { bg:"#DBEAFE", color:"#1E40AF" },
  "កំពុងប្រជុំ":         { bg:"#DBEAFE", color:"#1E40AF" },
  "កំណត់ហើយ":           { bg:"#DBEAFE", color:"#1E40AF" },
  "ពន្យារពេល":           { bg:"#FEF3C7", color:"#92400E" },
  "សេចក្តីព្រាង":       { bg:"#F3F4F6", color:"#6B7280" },
  "ត្រូវផ្ទៀងផ្ទាត់":  { bg:"#DBEAFE", color:"#1E40AF" },

  "ឈប់សម្រាក":          { bg:"#FEF3C7", color:"#92400E" },
  "មិនសកម្ម":           { bg:"#FEF3C7", color:"#92400E" },
  "កំពុងជួសជុល":        { bg:"#FEF3C7", color:"#92400E" },
  "កំពុងប្រើ":          { bg:"#FEF3C7", color:"#92400E" },

  "ផ្អាកការងារ":        { bg:"#FEE2E2", color:"#991B1B" },
  "ត្រូវបានបិទ":        { bg:"#FEE2E2", color:"#991B1B" },
  "បានចាក់សោ":          { bg:"#FEE2E2", color:"#991B1B" },
  "ផ្អាកប្លោក អាសន្ន": { bg:"#FEF3C7", color:"#92400E" },
  "ចូលនិវត្តន៍":        { bg:"#FEE2E2", color:"#991B1B" },
  "បានលាឈប់":           { bg:"#FEE2E2", color:"#991B1B" },
  "កិច្ចសន្យាផុត":      { bg:"#FEE2E2", color:"#991B1B" },
  "ផុតកំណត់":           { bg:"#FEE2E2", color:"#991B1B" },
  "បោះបង់":             { bg:"#FEE2E2", color:"#991B1B" },
  "បានបញ្ចប់":          { bg:"#F3F4F6", color:"#6B7280" },
  "បិទដំណើរការ":        { bg:"#FEE2E2", color:"#991B1B" },
};

export function StatusBadge({ status }) {
  const { t } = useLanguage();
  const s = statusMap[status] || { bg:"#F3F4F6", color:"#374151" };
  return (
    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600,
                   background:s.bg, color:s.color, whiteSpace:"nowrap" }}>
      {t(status)}
    </span>
  );
}

export function Modal({ title, subtitle, onClose, children, width = 500 }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)",
                  display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
      <div style={{ background:"var(--surface)", borderRadius:16, padding:28,
                    width, maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto", scrollPaddingTop:60,
                    boxShadow:"0 24px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
          <div>
            <h3 style={{ fontSize:16, fontWeight:700, color:"var(--brown2)",
                         fontFamily:"'Moul','Noto Sans Khmer',serif" }}>{title}</h3>
            {subtitle && <p style={{ fontSize:11, color:"var(--muted)", marginTop:2 }}>{subtitle}</p>}
          </div>
          <button onClick={onClose}
            style={{ background:"none", border:"none", cursor:"pointer", color:"var(--muted)", lineHeight:1, marginLeft:16, display:"flex", alignItems:"center", padding:0 }}><MdClose size={20}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function FormField({ label, type="text", value, onChange, options, placeholder, rows }) {
  const { t } = useLanguage();
  const base = {
    width:"100%", padding:"9px 12px",
    border:"1px solid var(--border)", borderRadius:8, fontSize:13, outline:"none",
    background:"var(--surface)", boxSizing:"border-box",
    fontFamily:"'Khmer OS Siemreap','Noto Sans Khmer',sans-serif",
  };
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:"block", fontSize:11, color:"var(--muted)", marginBottom:4 }}>{label}</label>
      {options ? (
        <select value={value ?? ""} onChange={e => onChange(e.target.value)} style={base}>
          <option value="">{t("ជ្រើសរើស...")}</option>
          {options.map(o =>
            typeof o === "object"
              ? <option key={o.value} value={o.value}>{o.label}</option>
              : <option key={o} value={o}>{o}</option>
          )}
        </select>
      ) : rows ? (
        <textarea value={value} onChange={e => onChange(e.target.value)}
          rows={rows} placeholder={placeholder || ""}
          style={{ ...base, resize:"vertical" }} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder || ""}
          style={base} />
      )}
    </div>
  );
}

export function Btn({ onClick, children, variant="primary", style={} }) {
  const base = { padding:"9px 20px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none" };
  const variants = {
    primary: { background:"var(--brown)", color:"#fff" },
    outline: { background:"var(--surface)",    color:"var(--brown)", border:"1px solid var(--border)" },
    danger:  { background:"#FEE2E2", color:"#991B1B" },
  };
  return (
    <button onClick={onClick} style={{ ...base, ...variants[variant], ...style }}>{children}</button>
  );
}

export function StatCard({ label, value, color="var(--brown)" }) {
  return (
    <div style={{ background:"var(--surface)", borderRadius:10, padding:16, border:"1px solid var(--border)" }}>
      <p style={{ margin:"0 0 6px", fontSize:12, color:"var(--muted)" }}>{label}</p>
      <p style={{ margin:0, fontSize:28, fontWeight:700, color }}>{value}</p>
    </div>
  );
}

export function Th({ children }) {
  return (
    <th style={{ padding:"12px 16px", textAlign:"left", color:"var(--muted)", fontWeight:500,
                 borderBottom:"1px solid var(--border)", background:"var(--cream)", fontSize:12,
                 position:"sticky", top:0, zIndex:1 }}>
      {children}
    </th>
  );
}

// Wraps a <table> in a scrollable container so <Th> (position:sticky, top:0)
// keeps the header pinned while rows scroll underneath.
export function TableScroll({ children, maxHeight = 480, style = {} }) {
  return (
    <div style={{ maxHeight, overflow:"auto", border:"1px solid var(--border)",
                  borderRadius:10, ...style }}>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        {children}
      </table>
    </div>
  );
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export function Pagination({
  page, totalPages, totalItems, pageSize,
  onPageChange, onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}) {
  const { t } = useLanguage();
  const KH = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
  const from = totalItems === 0 ? 0 : page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, totalItems);
  const btn = (disabled) => ({
    padding:"6px 10px", borderRadius:6, border:"1px solid var(--border)",
    background: disabled ? "#F3F4F6" : "var(--surface)", color: disabled ? "#B8A597" : "var(--brown)",
    cursor: disabled ? "default" : "pointer", fontSize:13, lineHeight:1,
  });
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                  flexWrap:"wrap", gap:12, padding:"12px 16px", fontFamily:KH }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"var(--muted)" }}>
        <span>{t("ជួរដេកក្នុងមួយទំព័រ")}</span>
        <select
          value={pageSize}
          onChange={e => onPageSizeChange(Number(e.target.value))}
          style={{ padding:"4px 8px", borderRadius:6, border:"1px solid var(--border)",
                   fontSize:12, background:"var(--surface)", color:"var(--brown)" }}
        >
          {pageSizeOptions.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div style={{ fontSize:12, color:"var(--muted)" }}>
        {totalItems === 0 ? `0 ${t("លទ្ធផល")}` : `${from}–${to} ${t("នៃ")} ${totalItems}`}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
        <button disabled={page <= 0} onClick={() => onPageChange(page - 1)} style={btn(page <= 0)}>‹</button>
        <span style={{ fontSize:12, color:"var(--brown)", minWidth:60, textAlign:"center" }}>
          {Math.max(totalPages, 1) === 0 ? 0 : page + 1} / {Math.max(totalPages, 1)}
        </span>
        <button disabled={page + 1 >= totalPages} onClick={() => onPageChange(page + 1)} style={btn(page + 1 >= totalPages)}>›</button>
      </div>
    </div>
  );
}
export function Td({ children, muted }) {
  return (
    <td style={{ padding:"12px 16px", fontSize:13, color: muted ? "var(--muted)" : "var(--text)",
                 borderBottom:"1px solid var(--border)" }}>
      {children}
    </td>
  );
}
export function Can({ perm, action = "view", children }) {
  const { user } = useAuth();
  return canDo(user, perm, action) ? children : null;
}

export function ActionBtns({ onView, onEdit, onDelete, perm }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const allow = (action) => !perm || canDo(user, perm, action);
  const showView   = onView   && allow("view");
  const showEdit   = onEdit   && allow("edit");
  const showDelete = onDelete && allow("delete");
  if (!showView && !showEdit && !showDelete) return null;
  return (
    <td style={{ padding:"12px 16px", borderBottom:"1px solid var(--border)" }}>
      <div style={{ display:"flex", gap:4, alignItems:"center" }}>
        {showView   && <button onClick={onView}   title={t("មើល")}    style={{ background:"none", border:"none", cursor:"pointer", color:"#3B82F6", display:"flex", alignItems:"center", padding:"4px", borderRadius:4 }}><MdVisibility size={18} /></button>}
        {showEdit   && <button onClick={onEdit}   title={t("កែប្រែ")} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--muted)", display:"flex", alignItems:"center", padding:"4px", borderRadius:4 }}><MdEdit       size={18} /></button>}
        {showDelete && <button onClick={onDelete} title={t("លុប")}    style={{ background:"none", border:"none", cursor:"pointer", color:"#EF4444", display:"flex", alignItems:"center", padding:"4px", borderRadius:4 }}><MdDelete     size={18} /></button>}
      </div>
    </td>
  );
}

export function ThActions({ perm, actions = ["view", "edit", "delete"] }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  if (perm && !actions.some((a) => canDo(user, perm, a))) return null;
  return <Th>{t("សកម្មភាព")}</Th>;
}

export function Toast({ message, onHide, type = "success", duration = 3000 }) {
  const KH = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!message || hovered) return;
    const t = setTimeout(onHide, duration);
    return () => clearTimeout(t);
  }, [message, onHide, hovered, duration]);

  if (!message) return null;

  const isError = type === "error";
  const theme = isError
    ? { bg:"#FEE2E2", border:"#FCA5A5", icon:"#EF4444", text:"#991B1B", close:"#DC2626", Icon:MdError }
    : { bg:"#D1FAE5", border:"#6EE7B7", icon:"#10B981", text:"#065F46", close:"#059669", Icon:MdCheckCircle };

  return (
    <>
      <style>{`@keyframes _toast_in { from { transform:translateX(110%); opacity:0 } to { transform:translateX(0); opacity:1 } }`}</style>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
        position:"fixed", top:24, right:24, zIndex:3000,
        background:theme.bg, border:`1.5px solid ${theme.border}`,
        borderRadius:12, padding:"13px 18px",
        display:"flex", alignItems:"center", gap:10,
        boxShadow:"0 8px 28px rgba(0,0,0,0.13)", minWidth:240, maxWidth:340,
        animation:"_toast_in 0.25s ease",
      }}>
        <span style={{ width:28, height:28, borderRadius:"50%", background:theme.icon,
                        display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <theme.Icon size={16} color="#fff"/>
        </span>
        <span style={{ fontSize:13, color:theme.text, fontFamily:KH, flex:1, lineHeight:1.5 }}>
          {message}
        </span>
        <button onClick={onHide}
          style={{ background:"none", border:"none", cursor:"pointer",
                   color:theme.close, lineHeight:1, padding:"2px 4px",
                   display:"flex", alignItems:"center" }}>
          <MdClose size={17}/>
        </button>
      </div>
    </>
  );
}

export function ConfirmDialog({ open, message, onConfirm, onCancel,
  title = "បញ្ជាក់ការលុប",
  confirmLabel = "លុប",
  confirmColor = "#EF4444",
  icon = null,
  iconBg = "#FEE2E2",
  warning = "សកម្មភាពនេះមិនអាចត្រឡប់វិញបានទេ!",
}) {
  const { t } = useLanguage();
  if (!open) return null;
  const KH = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
  const MO = "'Moul','Noto Sans Khmer',serif";
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  zIndex:2000 }}>
      <div style={{ background:"var(--surface)", borderRadius:16, padding:"26px 28px 22px",
                    width:380, maxWidth:"90vw",
                    boxShadow:"0 24px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
          <div style={{ width:44, height:44, borderRadius:"50%", background:iconBg,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        flexShrink:0 }}>
            {icon || <MdDelete size={22} color="#EF4444"/>}
          </div>
          <h3 style={{ fontSize:15, fontWeight:700, color:"var(--text)",
                       fontFamily:MO, lineHeight:1.4 }}>
            {t(title)}
          </h3>
        </div>
        <p style={{ fontSize:13, color:"var(--brown)", fontFamily:KH,
                    lineHeight:1.65, marginBottom:10 }}>
          {message}
        </p>
        {warning && (
          <p style={{ fontSize:11, color:"#EF4444", fontFamily:KH,
                      background:"#FFF5F5", border:"1px solid #FECACA",
                      borderRadius:8, padding:"7px 10px", marginBottom:20,
                      display:"flex", alignItems:"center", gap:6 }}>
            <MdWarning size={14} style={{ flexShrink:0 }}/> {t(warning)}
          </p>
        )}
        <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
          <button onClick={onCancel}
            style={{ padding:"9px 22px", borderRadius:8, fontSize:13, cursor:"pointer",
                     background:"var(--surface)", color:"var(--brown)", border:"1px solid var(--border)",
                     fontFamily:KH }}>
            {t("បោះបង់")}
          </button>
          <button onClick={onConfirm}
            style={{ padding:"9px 22px", borderRadius:8, fontSize:13, fontWeight:600,
                     cursor:"pointer", background:confirmColor, color:"#fff",
                     border:"none", fontFamily:KH }}>
            {t(confirmLabel)}
          </button>
        </div>
      </div>
    </div>
  );
}